import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

// Types for Alpha Fusion
export interface AlphaOpportunity {
    symbole: string;
    nom_societe: string;
    cours_actuel: number;
    score: number;
    signal_type: string;
    entry_price: number;
    target_price: number | string;
    stop_loss: number;
    position_size: number;
    position_value: number;
    justification: string;
    // Specifics
    nvi_bullish: boolean;
    guppy_bullish: boolean;
    is_bull_flag: boolean;
    trix: number;
}

export interface PortfolioSummary {
    cash_available: number;
    cash_blocked_j2: number;
    total_portfolio_value: number;
    open_swing_positions: number;
    max_position_value: number;
    risk_per_trade: number;
}

export interface EligibleStock {
    symbole: string;
    nom_societe: string;
    cours_actuel: number;
    score: number;
    status: string; // "NEUTRAL", "BULLISH_TREND", "ALPHA_MODE"
    is_opportunity: boolean;
    is_owned: boolean;
    // Data
    nvi_bullish: boolean;
    guppy_bullish: boolean;
    is_bull_flag: boolean;
    trix: number;
}

export interface AlphaFusionData {
    generated_at: string;
    portfolio_summary: PortfolioSummary;
    opportunities: AlphaOpportunity[];
    sell_signals: any[];
    eligible_universe: EligibleStock[];
    market_sentiment: string;
}

export const useAlphaFusion = () => {
    const { token } = useAuth();
    const [data, setData] = useState<AlphaFusionData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/portfolio/swing-trader/alpha-fusion`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors du chargement');
            }

            const jsonData = await response.json();
            setData(jsonData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur réseau');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, loading, error, refetch: fetchData };
};
